import React, { useState, useEffect, useCallback } from 'react';
import { Home, ShieldCheck, Phone, BookOpen, Gauge, Hash, Briefcase, Network } from 'lucide-react';
import PVSPageLayout from './PVSPageLayout';
import CalorLineOfContact from './CalorLineOfContact';
import CalorPDFViewer from './CalorPDFViewer';
import CalorCDEForm from './calor/CalorCDEForm';
import CalorCertificateRangeModal from './calor/CalorCertificateRangeModal';
import fetchUltrasonicDailyCheckStatus from '../services/ultrasonicDailyCheckStatusService';

const CAL515_PT1_FILENAME = 'cal515-pt1.pdf';
const CAL515_PT5_FILENAME = 'cal515-pt5.pdf';
const CALOR_UT_GUIDE_FILENAME = 'calor-ut-guide.pdf';
const CALOR_VID_MANUAL_FILENAME = 'calor-vid-manual.pdf';
const DVI_JOB_DESCRIPTION_FILENAME = 'q-qa-07-jd-dvi.pdf';
const INSPECTION_BODY_ORG_FILENAME = 'q-qa-07-org-01.pdf';
const VESSEL_INSPECTION_ORG_FILENAME = 'q-qa-07-org-03.pdf';

const isAuthorisedForCalor = () => {
  try {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) return false;
    JSON.parse(userData);
    return true;
  } catch {
    return false;
  }
};

const isCurrentUserAdmin = () => {
  try {
    const userData = localStorage.getItem('user');
    if (!userData) return false;
    const user = JSON.parse(userData);
    return user?.isAdmin === true;
  } catch {
    return false;
  }
};

const CalorGasHub = ({ onReturnHome, onOpenTankCertification }) => {
  const [view, setView] = useState('menu');
  const [isAdmin] = useState(() => isCurrentUserAdmin());
  const [rangeModalOpen, setRangeModalOpen] = useState(false);
  const [gateStatus, setGateStatus] = useState(null);
  const [gateMessage, setGateMessage] = useState('');

  const refreshGateStatus = useCallback(async () => {
    try {
      const status = await fetchUltrasonicDailyCheckStatus();
      setGateStatus(status);
      return status;
    } catch (error) {
      console.error('Failed to load ultrasonic daily check status:', error);
      setGateStatus(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshGateStatus();
  }, [refreshGateStatus]);

  useEffect(() => {
    if (!isAuthorisedForCalor() && typeof onReturnHome === 'function') {
      onReturnHome();
    }
  }, [onReturnHome]);

  useEffect(() => {
    if (view === 'cdeCert' && gateStatus && !gateStatus.calorAndTankCertUnlocked) {
      setGateMessage('Complete the Ultrasonic Daily Check before opening CDE Vessel Certification.');
      setView('menu');
    }
  }, [view, gateStatus]);

  const requireAuthThen = (fn) => () => {
    if (!isAuthorisedForCalor()) {
      if (typeof onReturnHome === 'function') onReturnHome();
      return;
    }
    fn();
  };

  const openCdeCert = () => {
    if (gateStatus && !gateStatus.calorAndTankCertUnlocked) {
      setGateMessage('Complete the Ultrasonic Daily Check before opening CDE Vessel Certification.');
      return;
    }
    setGateMessage('');
    setView('cdeCert');
  };

  const handleOpenTankCertification = () => {
    if (gateStatus && !gateStatus.calorAndTankCertUnlocked) {
      setGateMessage('Complete the Ultrasonic Daily Check before opening Tank Certification.');
      return;
    }
    if (typeof onOpenTankCertification === 'function') {
      onOpenTankCertification();
    }
  };

  if (view === 'cal515pt1') {
    return (
      <CalorPDFViewer
        title="CAL515 PT1"
        filename={CAL515_PT1_FILENAME}
        onBack={() => setView('menu')}
        onReturnHome={onReturnHome}
      />
    );
  }
  if (view === 'cal515pt5') {
    return (
      <CalorPDFViewer
        title="CAL515 PT5"
        filename={CAL515_PT5_FILENAME}
        onBack={() => setView('menu')}
        onReturnHome={onReturnHome}
      />
    );
  }
  if (view === 'calorUTGuide') {
    return (
      <CalorPDFViewer
        title="Calor UT Guide"
        filename={CALOR_UT_GUIDE_FILENAME}
        onBack={() => setView('menu')}
        onReturnHome={onReturnHome}
      />
    );
  }
  if (view === 'calorVidManual') {
    return (
      <CalorPDFViewer
        title="Calor VID Manual"
        filename={CALOR_VID_MANUAL_FILENAME}
        onBack={() => setView('menu')}
        onReturnHome={onReturnHome}
      />
    );
  }
  if (view === 'dviJobDescription') {
    return (
      <CalorPDFViewer
        title="Depot Vessel Inspector Job Description"
        filename={DVI_JOB_DESCRIPTION_FILENAME}
        onBack={() => setView('menu')}
        onReturnHome={onReturnHome}
      />
    );
  }
  if (view === 'inspectionBodyOrg') {
    return (
      <CalorPDFViewer
        title="Inspection Body"
        filename={INSPECTION_BODY_ORG_FILENAME}
        onBack={() => setView('menu')}
        onReturnHome={onReturnHome}
      />
    );
  }
  if (view === 'vesselInspectionOrg') {
    return (
      <CalorPDFViewer
        title="Vessel Inspection"
        filename={VESSEL_INSPECTION_ORG_FILENAME}
        onBack={() => setView('menu')}
        onReturnHome={onReturnHome}
      />
    );
  }
  if (view === 'cdeCert') {
    if (gateStatus && !gateStatus.calorAndTankCertUnlocked) {
      return null;
    }
    return (
      <CalorCDEForm
        onBack={() => setView('menu')}
        onOpenTankCertification={handleOpenTankCertification}
        onOpenLineOfContact={() => setView('contact')}
        tankCertificationUnlocked={gateStatus?.calorAndTankCertUnlocked !== false}
      />
    );
  }
  if (view === 'contact') {
    return <CalorLineOfContact onBack={() => setView('menu')} />;
  }

  const items = [
    {
      key: 'cal515pt1',
      title: 'CAL515 PT1',
      description: 'Calor CAL515 Part 1 document',
      icon: BookOpen,
    },
    {
      key: 'cal515pt5',
      title: 'CAL515 PT5',
      description: 'Calor CAL515 Part 5 document',
      icon: BookOpen,
    },
    {
      key: 'calorUTGuide',
      title: 'Calor UT Guide',
      description: 'Calor Ultrasonic Testing guide',
      icon: Gauge,
    },
    {
      key: 'calorVidManual',
      title: 'Calor VID Manual',
      description: 'Vessel Identity Manual (February 2022)',
      icon: BookOpen,
    },
    {
      key: 'dviJobDescription',
      title: 'Depot Vessel Inspector Job Description',
      description: 'Q-QA-07-JD-DVI (Rev 1.00)',
      icon: Briefcase,
    },
    {
      key: 'inspectionBodyOrg',
      title: 'Inspection Body',
      description: 'Q-QA-07-ORG-01 (Rev 2.08)',
      icon: Network,
    },
    {
      key: 'vesselInspectionOrg',
      title: 'Vessel Inspection',
      description: 'Q-QA-07-ORG-03 (Rev 2.02)',
      icon: Network,
    },
    {
      key: 'cdeCert',
      title: 'CDE Vessel Certification',
      description: 'Calor Gas Bulk Tank Periodic Examination form (CGL104228)',
      icon: ShieldCheck,
      requiresUltrasonicCheck: true,
    },
    {
      key: 'contact',
      title: 'Calor Line of Contact',
      description: 'Key Calor contacts',
      icon: Phone,
    },
  ];

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex justify-between items-start mb-8 gap-4">
          <h1 className="text-3xl font-bold text-white leading-tight">Calor Gas</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isAdmin && (
              <button
                type="button"
                onClick={() => setRangeModalOpen(true)}
                className="pvs-glass pvs-glass-hover flex items-center gap-2 px-4 h-12"
                title="Configure CDE certificate numbers"
              >
                <Hash className="h-5 w-5" stroke="url(#icon-two-tone)" />
                <span className="text-sm text-white hidden sm:inline">Certificate Numbers</span>
              </button>
            )}
            <button
              type="button"
              onClick={onReturnHome}
              className="pvs-glass pvs-glass-hover flex items-center justify-center w-12 h-12"
              title="Return to home"
            >
              <Home className="h-5 w-5" stroke="url(#icon-two-tone)" />
            </button>
          </div>
        </div>

        {gateStatus && !gateStatus.calorAndTankCertUnlocked && (
          <div className="mb-4 p-4 rounded-lg border border-amber-500/40 bg-amber-900/20 text-amber-100">
            <p className="text-sm font-medium">
              Ultrasonic daily check required — CDE Vessel Certification is locked until today&apos;s check is submitted in Forms.
            </p>
          </div>
        )}

        {gateStatus?.testingBypassActive && (
          <div className="mb-4 p-4 rounded-lg border border-purple-500/40 bg-purple-900/20 text-purple-100">
            <p className="text-sm font-medium">
              Testing mode — certificate numbering is disabled, so the Ultrasonic Daily Check requirement is
              bypassed. Re-enable numbering to restore the normal rules.
            </p>
          </div>
        )}

        {gateStatus?.completedToday && gateStatus.completedBy && (
          <div className="mb-4 p-4 rounded-lg border border-green-500/30 bg-green-900/20 text-green-100">
            <p className="text-sm">
              Today&apos;s ultrasonic check completed by {gateStatus.completedBy}.
            </p>
          </div>
        )}

        {gateMessage && (
          <div className="mb-4 p-4 rounded-lg border border-red-500/30 bg-red-900/20 text-red-200">
            <p className="text-sm font-medium">{gateMessage}</p>
          </div>
        )}

        <div className="grid gap-4">
          {items.map((item) => {
            const Icon = item.icon;
            const locked = item.requiresUltrasonicCheck && gateStatus && !gateStatus.calorAndTankCertUnlocked;
            const onClick = item.key === 'cdeCert'
              ? requireAuthThen(openCdeCert)
              : requireAuthThen(() => setView(item.key));

            return (
              <button
                key={item.key}
                type="button"
                disabled={locked}
                className={`w-full pvs-glass flex items-center px-5 py-5 text-left ${
                  locked ? 'opacity-50 cursor-not-allowed' : 'pvs-glass-hover'
                }`}
                onClick={onClick}
              >
                <div className="flex items-center space-x-4 w-full">
                  <div
                    className="p-3 rounded-2xl flex-shrink-0"
                    style={{
                      background: 'rgba(74, 222, 128, 0.06)',
                      border: '1px solid rgba(74, 222, 128, 0.15)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <Icon className="h-7 w-7" stroke="url(#icon-two-tone)" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                    <p className="text-sm text-pvs-text-muted">{item.description}</p>
                    {locked && (
                      <p className="text-xs text-amber-300 mt-2">
                        Complete the Ultrasonic Daily Check first.
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <CalorCertificateRangeModal
        isOpen={rangeModalOpen}
        onClose={() => {
          setRangeModalOpen(false);
          setGateMessage('');
          refreshGateStatus();
        }}
      />
    </PVSPageLayout>
  );
};

export default CalorGasHub;
